import type { UncleKind } from './indic';
import type { Kin, Sex } from './types';

/**
 * What to call someone (PRD 7.2.4): the forms of address families use, which often differ from the
 * relationship word on the card. A father's younger brother is "Chacha" but you call him "Chachaji" or
 * "Chachu"; an English uncle is "Uncle Ron". Younger relatives, cousins your age and most in-laws are called
 * by name, so they get no list.
 *
 * `addressRole` works out, without any language, which kind of address applies; each word set supplies the
 * forms for each role (`AddressWords`). A form may contain `{name}` for the person's first name ("Uncle
 * {name}"); those forms are left out when the name is unknown. Word sets written in their own script use no
 * `{name}` forms, since names are usually typed in English letters.
 */

export type AddressRole =
  | { r: 'parent'; sex: Sex }
  /** `collateral`: a grandparent's brother or sister (or cousin), not a grandparent in the direct line. */
  | { r: 'grandparent'; side: Sex; sex: Sex; great: boolean; collateral: boolean }
  /** An elder brother or sister, or an elder cousin (Bhaiya, Didi). */
  | { r: 'elder'; sex: Sex; maybe?: boolean }
  /** A parent's brother or sister, or a parent's cousin (Kaka, Mausi, Uncle Ron). */
  | { r: 'uncle'; kind: UncleKind | null; side: Sex; sex: Sex; elder: boolean | null }
  /** The spouse of a parent's brother or sister (Kaki, Mausa). */
  | { r: 'uncleSpouse'; kind: UncleKind | null; side: Sex; sex: Sex; elder: boolean | null }
  | { r: 'parentInLaw'; sex: Sex }
  /** A spouse's elder brother or sister (Bhaisahab for a husband's elder brother). */
  | { r: 'elderInLaw'; sex: Sex; spouseSex: Sex; maybe?: boolean }
  /** An elder brother's wife or an elder sister's husband (Bhabhi, Jijaji). */
  | { r: 'elderSiblingSpouse'; sex: Sex; maybe?: boolean }
  /** A spouse's elder sibling's spouse: a husband's elder brother's wife (Didi, Bhabi ji), a spouse's elder sister's husband (Jija ji). */
  | { r: 'coSiblingInLaw'; sex: Sex; spouseSex: Sex; maybe?: boolean }
  /** A younger (or age unknown) sibling's spouse, or a spouse's younger sibling: by name, or an age-neutral word (cuñado). */
  | { r: 'siblingInLaw'; sex: Sex }
  | { r: 'coParentInLaw'; sex: Sex }
  /** Called by their first name. */
  | { r: 'name' };

type MFs = { m: string[]; f: string[] };

/** A first name written in English letters (the default for `{name}` forms). */
export const LATIN_NAME = /^[\p{Script=Latin}'’-]+$/u;

export interface AddressWords {
  /**
   * `{name}` forms are used only when the first name is written in this script, so a Gujarati-script set says
   * "રોહનભાઈ" for a name typed in Gujarati and leaves the name out for one typed in English letters.
   */
  nameScript?: RegExp;
  parent: MFs;
  /** By side (the father's or the mother's) and sex; `great` for great-grandparents and beyond. */
  grandparent: (side: Sex, sex: Sex, great: boolean) => string[];
  /**
   * A grandparent's brother or sister. Indian languages use the grandparent word (Dadaji); English and
   * Spanish say Uncle Frank, tío Frank. Without it, the grandparent forms are used.
   */
  grandUncle?: MFs;
  elder: MFs;
  /** `elder` forms are for the father's elder brother and his wife (Tauji, Taiji); others use `any`. */
  uncle: Record<UncleKind, { elder?: string[]; any: string[] }>;
  uncleSpouse: Record<UncleKind, { elder?: string[]; any: string[] }>;
  parentInLaw: MFs;
  /** A spouse's elder sibling; by default the same as one's own (`elder`). */
  elderInLaw?: (sex: Sex, spouseSex: Sex) => string[];
  /** m: an elder sister's husband; f: an elder brother's wife. */
  elderSiblingSpouse: MFs;
  /**
   * In-laws of one's own generation whatever their age, where a language has a word for that (Spanish cuñado).
   * Without it, a younger sibling's spouse and a spouse's younger sibling are called by name.
   */
  siblingInLaw?: MFs;
  /** A spouse's elder sibling's spouse, by their sex and the spouse's; without it, by name. */
  coSiblingInLaw?: (sex: Sex, spouseSex: Sex) => string[];
  coParentInLaw: MFs;
}

const kindOf = (side: Sex, sex: Sex): UncleKind | null =>
  side && sex ? (`${side === 'm' ? 'F' : 'M'}${sex === 'm' ? 'B' : 'Z'}` as UncleKind) : null;

/** Which kind of address applies to `k`, or null for yourself and strangers. */
export function addressRole(k: Kin): AddressRole | null {
  const name = { r: 'name' } as const;
  switch (k.kind) {
    case 'self':
    case 'none':
      return null;
    case 'coParentInLaw':
      return { r: 'coParentInLaw', sex: k.sex };
    case 'blood':
      return blood(k.path, k.shape!, k.sex, k.older) ?? name;
    case 'marriage':
      if (k.via === 'both') {
        // [my spouse, their sibling, that sibling's spouse]: by the sibling's age against my spouse.
        const [spouse, sib] = k.path;
        return byAge(sib!.elder, { r: 'coSiblingInLaw', sex: k.sex, spouseSex: spouse!.sex }, null) ?? name;
      }
      return (k.via === 'mine' ? spouseSide(k) : relativesSpouse(k)) ?? name;
    default:
      return name; // spouse, step-sibling, other relatives
  }
}

function blood(steps: Kin['path'], shape: NonNullable<Kin['shape']>, sex: Sex, older: boolean | null): AddressRole | null {
  if ('up' in shape) return shape.up === 1 ? { r: 'parent', sex } : { r: 'grandparent', side: steps[0]!.sex, sex, great: shape.up >= 3, collateral: false };
  if ('down' in shape) return null;
  const { a, b } = shape;
  const g = b - a;
  const side = steps[0]!.sex;
  // `maybe`: their age against yours is unknown (no birth year), so they may be older or younger.
  if (a === 1 && b === 1) return byAge(steps[0]!.elder, { r: 'elder', sex }, null);
  if (g === -1) return { r: 'uncle', kind: kindOf(side, sex), side, sex, elder: a === 2 ? steps[1]!.elder : null };
  if (g <= -2) return { r: 'grandparent', side, sex, great: g <= -3, collateral: true };
  if (g === 0) return byAge(older, { r: 'elder', sex }, null);
  return null;
}

/** The elder role when older, the same role marked `maybe` when the age is unknown, else `younger`. */
type ByAgeRole = Extract<AddressRole, { r: 'elder' | 'elderInLaw' | 'elderSiblingSpouse' | 'coSiblingInLaw' }>;
function byAge(older: boolean | null, elder: ByAgeRole, younger: AddressRole | null): AddressRole | null {
  return older === true ? elder : older === null ? { ...elder, maybe: true } : younger;
}

/** My spouse's family: their parents and grandparents, uncles and aunts, and elder brothers and sisters. */
function spouseSide(k: Kin): AddressRole | null {
  const [spouse, ...rest] = k.path;
  const shape = k.shape!;
  if ('up' in shape) return shape.up === 1 ? { r: 'parentInLaw', sex: k.sex } : { r: 'grandparent', side: rest[0]!.sex, sex: k.sex, great: shape.up >= 3, collateral: false };
  if ('down' in shape) return null;
  const { a, b } = shape;
  const g = b - a;
  if (a === 1 && b === 1) return byAge(rest[0]!.elder, { r: 'elderInLaw', sex: k.sex, spouseSex: spouse!.sex }, { r: 'siblingInLaw', sex: k.sex });
  if (g === -1) return { r: 'uncle', kind: kindOf(rest[0]!.sex, k.sex), side: rest[0]!.sex, sex: k.sex, elder: a === 2 ? rest[1]!.elder : null };
  if (g <= -2) return { r: 'grandparent', side: rest[0]!.sex, sex: k.sex, great: g <= -3, collateral: true };
  return null;
}

/** My relatives' spouses: uncles' and aunts' spouses, grandparents' spouses, elder siblings' spouses. */
function relativesSpouse(k: Kin): AddressRole | null {
  const shape = k.shape!;
  const rel = k.path.slice(0, -1);
  const relative = rel[rel.length - 1]!;
  if ('up' in shape) return shape.up === 1 ? null : { r: 'grandparent', side: rel[0]!.sex, sex: k.sex, great: shape.up >= 3, collateral: false };
  if ('down' in shape) return null;
  const { a, b } = shape;
  const g = b - a;
  if (a === 1 && b === 1) return byAge(relative.elder, { r: 'elderSiblingSpouse', sex: k.sex }, { r: 'siblingInLaw', sex: k.sex });
  // An elder cousin's spouse is addressed like an elder brother's or sister's (Bhabhi, Jijaji).
  if (g === 0) return byAge(k.relativeOlder, { r: 'elderSiblingSpouse', sex: k.sex }, null);
  if (g === -1) {
    const side = rel[0]!.sex;
    return { r: 'uncleSpouse', kind: kindOf(side, relative.sex), side, sex: k.sex, elder: a === 2 ? rel[1]!.elder : null };
  }
  if (g <= -2) return { r: 'grandparent', side: rel[0]!.sex, sex: k.sex, great: g <= -3, collateral: true };
  return null;
}

const both = (w: MFs, sex: Sex) => (sex === 'm' ? w.m : sex === 'f' ? w.f : [...w.m, ...w.f]);

/** Uncle or aunt forms by kind; with side or gender unknown, every kind that fits. */
function uncleForms(table: AddressWords['uncle'], role: { kind: UncleKind | null; side: Sex; sex: Sex; elder: boolean | null }): string[] {
  if (role.kind) {
    const t = table[role.kind];
    return role.elder === true && t.elder ? t.elder : t.any;
  }
  const kinds = (['FB', 'FZ', 'MB', 'MZ'] as UncleKind[]).filter((k) => (!role.side || k[0] === (role.side === 'm' ? 'F' : 'M')) && (!role.sex || k[1] === (role.sex === 'm' ? 'B' : 'Z')));
  return kinds.flatMap((k) => table[k].any);
}

/** What to call someone: forms of address, and whether they might instead be called by name. */
export interface Address {
  /** Most usual first; empty when they are called by name (and for yourself). */
  forms: string[];
  /**
   * Their age against yours is unknown, and if they are younger they'd be called by name: the card shows
   * "Deepeshbhai / Deepesh" and the profile says a birth year settles it.
   */
  orByName: boolean;
}

/**
 * The forms of address for `k` in a word set. `{name}` becomes the person's first name, and forms with it are
 * dropped when there is none or it is written in another script.
 */
export function addressFor(w: AddressWords, k: Kin, firstName: string | null | undefined): Address {
  const role = addressRole(k);
  if (!role) return { forms: [], orByName: false };
  const first = firstName?.trim().split(/\s+/)[0];
  const usable = first && (w.nameScript ?? LATIN_NAME).test(first) ? first : null;
  const fill = (forms: string[]) => [...new Set(forms.flatMap((f) => (f.includes('{name}') ? (usable ? [f.split('{name}').join(usable)] : []) : [f])))];
  const elder = fill(formsFor(w, role));
  if (!('maybe' in role && role.maybe) || elder.length === 0) return { forms: elder, orByName: false };
  // Age unknown: if the younger counterpart has its own word (cuñado), list both; otherwise "or by name".
  const younger = role.r === 'elderInLaw' || role.r === 'elderSiblingSpouse' ? fill(w.siblingInLaw ? both(w.siblingInLaw, role.sex) : []) : [];
  return younger.length ? { forms: [...new Set([...elder, ...younger])], orByName: false } : { forms: elder, orByName: true };
}

function formsFor(w: AddressWords, role: AddressRole): string[] {
  let forms: string[];
  switch (role.r) {
    case 'parent':
      forms = both(w.parent, role.sex);
      break;
    case 'grandparent': {
      if (role.collateral && w.grandUncle) {
        forms = both(w.grandUncle, role.sex);
        break;
      }
      const sides: Sex[] = role.side ? [role.side] : ['m', 'f'];
      const sexes: Sex[] = role.sex ? [role.sex] : ['m', 'f'];
      forms = sides.flatMap((s) => sexes.flatMap((x) => w.grandparent(s, x, role.great)));
      break;
    }
    case 'elder':
      forms = both(w.elder, role.sex);
      break;
    case 'uncle':
      forms = uncleForms(w.uncle, role);
      break;
    case 'uncleSpouse': {
      // With the uncle's or aunt's gender unknown, their spouse's is the opposite: Kaki is a woman, Fuva a man.
      const uncleSex: Sex = role.kind ? null : role.sex === 'f' ? 'm' : role.sex === 'm' ? 'f' : null;
      forms = uncleForms(w.uncleSpouse, { ...role, sex: role.kind ? role.sex : uncleSex });
      break;
    }
    case 'parentInLaw':
      forms = both(w.parentInLaw, role.sex);
      break;
    case 'elderInLaw':
      forms = w.elderInLaw ? w.elderInLaw(role.sex, role.spouseSex) : both(w.elder, role.sex);
      break;
    case 'elderSiblingSpouse':
      forms = both(w.elderSiblingSpouse, role.sex);
      break;
    case 'siblingInLaw':
      forms = w.siblingInLaw ? both(w.siblingInLaw, role.sex) : [];
      break;
    case 'coSiblingInLaw':
      forms = w.coSiblingInLaw ? w.coSiblingInLaw(role.sex, role.spouseSex) : [];
      break;
    case 'coParentInLaw':
      forms = both(w.coParentInLaw, role.sex);
      break;
    case 'name':
      forms = [];
  }
  return forms;
}

/**
 * The one form for a card: the first that says more than the relationship word, else the word itself
 * ("Call: Fuva"), so every elder has a line. With the age unknown, the name follows: "Deepeshbhai / Deepesh".
 * Empty when they are called by name.
 */
export function cardAddress(addr: Address, label: string, firstName?: string | null): string {
  if (addr.forms.length === 0) return '';
  const same = (a: string) => a.toLocaleLowerCase() === label.toLocaleLowerCase();
  const form = addr.forms.find((f) => !same(f)) ?? addr.forms[0]!;
  const first = firstName?.trim().split(/\s+/)[0];
  return addr.orByName && first ? `${form} / ${first}` : form;
}
